import { MAX_LISTINGS_PRICE } from "constants/listings";
import { SIZES_LIST, SIZES_MAPPINGS } from "constants/product-sizes";
import { toCurrency } from "shared/helpers";

export const getListingSizes = ({ sizes }) => sizes.map(size => { return {
    label: Object.keys(size)[0],
    value: Object.values(size)[0]
  } });

export const formatListingImages = (images, displayImage) => [displayImage, ...images.filter(image => image.id !== displayImage?.id)];

export const getCondition = listing => (!listing.condition || listing.condition === "New" ? "New" : "Preloved");

export const formatListingInfo = listing => {
  const {
    productvariation,
    productvariation: { product }
  } = listing;

  return [
    { label: "Brand", value: product.display_brand },
    { label: "Product", value: productvariation.display_name },
    { label: "SKU", value: product.sku ?? productvariation.sku ?? productvariation.user_sku },
    { label: "Color", value: productvariation.color },
    { label: "Composition", value: product.composition ?? productvariation.options.replace(/composition:/gi, "") },
    {
      label: "Listing Price",
      // TODO: Issue MRP-2772
      // eslint-disable-next-line unicorn/consistent-destructuring
      value: toCurrency(listing.discounted_price || listing.unit_price || listing.productvariation.price)
    },
    {
      label: "Discounted Price",
      // TODO: Issue MRP-2772
      // eslint-disable-next-line unicorn/consistent-destructuring
      value: toCurrency(listing.discounted_price)
    },
    { label: "Wholesale Price", value: toCurrency(productvariation.price) },
    { label: "Condition", value: getCondition(listing) },
    { label: "Description", value: product.description }
  ];
};

export const sizeSortCompare = (a, b) => {
  if (SIZES_LIST.includes(a)) {
    if (SIZES_LIST.indexOf(a) > SIZES_LIST.indexOf(b)) return 1;
    if (SIZES_LIST.indexOf(b) > SIZES_LIST.indexOf(a)) return -1;
    return 0;
  }

  if (Number(a) > Number(b)) {
    return 1;
  }

  if (Number(a) === Number(b)) {
    return a > b ? -1 : 0;
  }

  return -1;
};

export const sortedSizesObjectArray = sizesArray => {
  if (!sizesArray || sizesArray.length === 0) return [];

  return sizesArray.sort((a, b) => sizeSortCompare(a.label, b.label));
};

export const sortedSizes = sizes => {
  if (!sizes || sizes.length === 0) return [];

  return sizes.sort(sizeSortCompare);
};

// TODO: Issue MRP-2772
// eslint-disable-next-line unicorn/no-array-reduce
export const formatListingSizes = sizes => sizes.reverse().reduce((acc, crt) => {
    acc[crt] = 0;
    return acc;
  }, {});

export const formatListingErrors = errors => Object.values(errors).map(item => (Array.isArray(item) ? item[0] : item));

export const getListingAvailableSizes = (sizes, productSizes) => {
  const availableSizes = {};
  for (const size of productSizes) {
    availableSizes[size.label] = size.val;
  }
  for (const size of sizes) {
    const label = Object.keys(size)[0];
    availableSizes[label] = size[label];
  }
  return availableSizes;
};

export const formatConfirmListingInfo = (listing, selectedSizes) => {
  const {
    productvariation,
    productvariation: { product }
  } = listing;
  return [
    { label: "Brand", value: product.display_brand },
    { label: "Collection", value: product.collection_name },
    { label: "SKU", value: product.sku },
    { label: "Name", value: productvariation.display_name },
    { label: "Color", value: productvariation.color },
    { label: "Description", value: productvariation.display_description },
    { label: "Composition", value: product.composition ?? productvariation.options.replace(/composition:/gi, "") },
    // TODO: Issue MRP-2772
    // eslint-disable-next-line no-use-before-define
    {
      label: "Size(s)",
      // TODO: Issue MRP-2775
      // eslint-disable-next-line no-use-before-define
      value: formatSelectedSizes(selectedSizes)
    },
    // TODO: Issue MRP-2772
    // eslint-disable-next-line unicorn/consistent-destructuring
    {
      label: "Cost",
      // TODO: Issue MRP-2775
      // eslint-disable-next-line unicorn/consistent-destructuring
      value: toCurrency(listing.unit_price || listing.productvariation.price)
    },
    // TODO: Issue MRP-2772
    // eslint-disable-next-line no-use-before-define, unicorn/consistent-destructuring
    {
      label: "Total value",
      // TODO: Issue MRP-2775
      // eslint-disable-next-line no-use-before-define, unicorn/consistent-destructuring
      value: getTotalValue(selectedSizes, listing.unit_price || listing.productvariation.price)
    }
  ];
};

export const formatSelectedSizes = sizes => {
  const result = Object.entries(sizes)
    .filter(size => Boolean(size[1]))
    .map(size => `${size[1]}/${size[0]}`);
  if (result.length > 0) return result.join(", ");
  return "No sizes selected";
};

export const getTotalValue = (sizes, price) => {
  const totalValue = Object.values(sizes).reduce((acc, curr) => acc + curr) * price;
  return toCurrency(totalValue);
};

export const formatPriceRange = ([priceMin, priceMax]) => {
  if (priceMin !== undefined && priceMax !== undefined) {
    return `${toCurrency(priceMin)} - ${toCurrency(priceMax)}`;
  }
  if (priceMax !== undefined) {
    return `${toCurrency(0)} - ${toCurrency(priceMax)}`;
  }
  if (priceMin !== undefined) {
    return `> ${toCurrency(priceMin)}`;
  }
  return "";
};

export const getIsCartItemsSwappable = items => items.every(
    ({ listing_type, brand_name }) => (listing_type === "swap" || "sell or swap") && brand_name === items[0].brand_name
  );

export const getIsOwnListingAvailableForSwap = (items, commonBrand) => items.some(item => (item.listing_type === "swap" || "sell or swap") && item.brand_name === commonBrand);

/**
 * Parses a number input while with two-digit precision.
 * Prevents unsafe results like NaN.
 * Coerces negative/infinite values into undefined so that they are treated as unset filters.
 */
export const normalizePrice = raw => {
  const price = Number.parseFloat(raw);
  // TODO: Issue MRP-2772
  // eslint-disable-next-line no-use-before-define
  if (!isSafeNumber(price) || price < 0 || MAX_LISTINGS_PRICE < price) {
    return;
  }
  // TODO: Issue MRP-2772
  // eslint-disable-next-line consistent-return
  return Number.parseFloat(price.toFixed(2));
};

export const isSafeNumber = n => typeof n === "number" && !Number.isNaN(n) && Number.isFinite(n);

const normalize = v => String(v).trim().toUpperCase();

/**
 * Returns true if the given product_sizes are a proper subset of a size chart in SIZES_MAPPINGS.
 *
 * A "proper subset" means:
 * - Every product size exists in the chart
 * - The product sizes do NOT exactly match the chart
 *
 * @param {Array<{ label: string }>} productSizes
 * @returns {boolean}
 */
export const isSubsetButNotFullChart = productSizes => {
  const inputSet = new Set(productSizes.map(s => normalize(s.label)));

  return Object.values(SIZES_MAPPINGS).some(group =>
    Object.values(group).some(chart => {
      const chartSet = new Set(chart.map(v => normalize(v)));

      const isSubset = [...inputSet].every(s => chartSet.has(s));
      const isExactMatch = inputSet.size === chartSet.size;

      return isSubset && !isExactMatch;
    })
  );
};

/**
 * Returns, per GROUPS key, the chart names that the given product_sizes are
 * a proper subset of (i.e. every existing size is in the chart, but the chart
 * still has sizes the product doesn't).
 *
 * @param {Array<{ label: string }>} productSizes
 * @returns {Partial<Record<string, string[]>>}
 */
export const getMatchingChartsByGroup = productSizes => {
  const inputSet = new Set(productSizes.map(s => normalize(s.label)));
  const result = {};

  for (const [group, charts] of Object.entries(SIZES_MAPPINGS)) {
    const matchingCharts = Object.entries(charts)
      .filter(([, chartSizes]) => {
        const chartSet = new Set(chartSizes.map(v => normalize(v)));
        const isSubset = [...inputSet].every(s => chartSet.has(s));
        const isExactMatch = inputSet.size === chartSet.size;
        return isSubset && !isExactMatch;
      })
      .map(([chartName]) => chartName);

    if (matchingCharts.length > 0) {
      // eslint-disable-next-line security/detect-object-injection
      result[group] = matchingCharts;
    }
  }

  return result;
};
